import { useCallback, useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  MoreHorizontalIcon,
  Edit02Icon,
  Copy01Icon,
  Delete02Icon,
  ClipboardIcon,
  ArrowDataTransferHorizontalIcon,
  Download01Icon,
} from "@hugeicons/core-free-icons"
import { downloadNodeOutput, isDownloadable } from "@/lib/utils/download"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { usePipelineStore } from "@/lib/stores/pipeline-store"
import { notify } from "@/components/ui/notification"

interface NodeMenuProps {
  nodeId: string
  nodeLabel: string
  nodeOutput: Record<string, unknown> | undefined
  onRename: () => void
}

export function NodeMenu({ nodeId, nodeLabel, nodeOutput, onRename }: NodeMenuProps) {
  const removeNode = usePipelineStore((s) => s.removeNode)
  const duplicateNode = usePipelineStore((s) => s.duplicateNode)
  const selectNode = usePipelineStore((s) => s.selectNode)
  const [open, setOpen] = useState(false)

  const handleDuplicate = useCallback(() => {
    const newId = duplicateNode(nodeId)
    if (newId) selectNode(newId)
  }, [nodeId, duplicateNode, selectNode])

  const handleDelete = useCallback(() => {
    removeNode(nodeId)
  }, [nodeId, removeNode])

  const handleDownload = useCallback(async () => {
    if (!nodeOutput) return
    try {
      await downloadNodeOutput(nodeOutput, nodeLabel)
      const type = typeof nodeOutput.text === "string" ? "Text file"
        : typeof nodeOutput.image === "string" ? "Image"
        : typeof nodeOutput.video === "string" ? "Video"
        : "Audio"
      notify.success("Download complete", { description: `${type} saved to your downloads.` })
    } catch {
      notify.error("Download failed", { description: "Could not export the output. Try again." })
    }
  }, [nodeOutput, nodeLabel])

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex size-5 items-center justify-center rounded-md opacity-0 group-hover/node:opacity-100 hover:bg-black/10 dark:hover:bg-white/10 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <HugeiconsIcon icon={MoreHorizontalIcon} size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={4}>
        <DropdownMenuItem onClick={onRename}>
          <HugeiconsIcon icon={Edit02Icon} className="size-4" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem disabled>
          <HugeiconsIcon icon={ArrowDataTransferHorizontalIcon} className="size-4" />
          Replace
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem disabled>
          <HugeiconsIcon icon={ClipboardIcon} className="size-4" />
          Cut
        </DropdownMenuItem>
        <DropdownMenuItem disabled>
          <HugeiconsIcon icon={Copy01Icon} className="size-4" />
          Copy
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDuplicate}>
          <HugeiconsIcon icon={Copy01Icon} className="size-4" />
          Duplicate
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleDownload}
          disabled={!isDownloadable(nodeOutput)}
        >
          <HugeiconsIcon icon={Download01Icon} className="size-4" />
          Download output
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem variant="destructive" onClick={handleDelete}>
          <HugeiconsIcon icon={Delete02Icon} className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
